import { createColumnHelper } from "@tanstack/react-table";
import { useGetAdminsList, useGetAdminTypes, useCreateAdmin, useUpdateAdmin, useDeleteAdmin } from "../../queries/admin";
import { useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Chip from "../../components/Chip";
import TanStackTable from "../../components/TanStackTable";
import { FaTrash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { getAdminType } from "../../utils/adminType";
const EditIcon = require("../../utils/media/icons/edit_icon.png");

const ListAdmins = () => {

    const adminType = getAdminType();

    const { data, isPending } = useGetAdminsList();
    const { data: adminTypes, isLoading: isLoadingTypes } = useGetAdminTypes();
    const { mutateAsync: createAdmin, isPending: isCreating } = useCreateAdmin();
    const { mutateAsync: updateAdmin, isPending: isUpdating } = useUpdateAdmin();
    const { mutateAsync: deleteAdmin, isPending: isDeleting } = useDeleteAdmin();

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm({
        defaultValues: {
            username: '',
            email: '',
            adminType: '',
        },
    });

    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('username', {
            header: 'Username',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-2">
                    {info.getValue() || 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor('email', {
            header: 'Email',
            cell: info => (
                <div className="whitespace-normal break-words line-clamp-2">
                    {info.getValue() || 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor('adminType.adminType', {
            header: () => <div className="text-center">Admin Type</div>,
            size: 10,
            align: 'center',
            cell: info => (
                <Chip
                    value={info.getValue() || 'N/A'}
                    color={info.getValue() === 'superadmin' ? 'blue' : 'green'}
                />
            ),
        }),
        columnHelper.accessor('viewAllTabs', {
            header: () => <div className="text-center">View All Tabs</div>,
            size: 10,
            align: 'center',
            cell: info => (
                <Chip
                    value={info.getValue() ? 'Yes' : 'No'}
                    color={info.getValue() ? 'green' : 'inactive'}
                />
            ),
        }),
        ...(adminType === 'superadmin' ? [
            columnHelper.accessor('actions', {
                header: () => <div className="text-center">Actions</div>,
                enableColumnFilter: false,
                cell: info => {
                    return (
                        <div className='flex items-center justify-center'>
                            <button
                                className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                                onClick={() => handleOpenEditModal(info.row.original)}
                            >
                                <img src={EditIcon} alt="edit" />
                            </button>
                            <button
                                className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
                                onClick={() => {
                                    handleDelete(info.row.original._id);
                                }}
                            >
                                <FaTrash className="fill-chipRed" />
                            </button>
                        </div>
                    )
                },
            })
        ] : []),
    ], [adminType]);

    // Handle delete functionality
    const handleDelete = async (adminId) => {
        const confirm = window.confirm("Are you sure you want to delete this admin?");
        if (!confirm) return;

        try {
            await deleteAdmin(adminId);
            toast.success('Admin deleted successfully');
        } catch (error) {
            toast.error(`Error deleting admin: ${error.message || 'Unknown error'}`);
        }
    };

    // Handle opening create modal
    const handleOpenCreateModal = () => {
        setIsEditMode(false);
        setSelectedAdmin(null);
        reset();
        setShowCreateModal(true);
    };

    // Handle opening edit modal
    const handleOpenEditModal = (admin) => {
        setIsEditMode(true);
        setSelectedAdmin(admin);
        setValue('username', admin.username);
        setValue('email', admin.email);
        setValue('adminType', admin.adminType?._id || '');
        setShowCreateModal(true);
    };

    // Handle closing modal
    const handleCloseModal = () => {
        setShowCreateModal(false);
        setSelectedAdmin(null);
        setIsEditMode(false);
        reset();
    };

    // Handle form submission
    const onSubmit = async (formData) => {
        try {
            // Prepare the data with adminTypeId instead of adminType string
            const submitData = {
                username: formData.username,
                email: formData.email,
                adminType: formData.adminType // This will now be the adminType ID
            };

            if (isEditMode && selectedAdmin) {
                await updateAdmin(
                    {
                        adminId: selectedAdmin._id,
                        adminData: submitData
                    },
                    {
                        onSuccess: (res) => {
                            if(res.error) {
                                toast.error(`Error updating admin: ${res.error.message || 'Unknown error'}`);
                                return;
                            } else {
                                toast.success('Admin updated successfully');
                            }
                        },
                        onError: (error) => {
                            toast.error(`Error updating admin: ${error.message || 'Unknown error'}`);
                        }
                    }
                );
            } else {
                await createAdmin(
                    submitData,
                    {
                        onSuccess: (res) => {
                            console.log(res);
                            if(res.error) {
                                toast.error(`Error creating admin: ${res.error.message || 'Unknown error'}`);
                                return;
                            } else {
                                toast.success('Admin created successfully');
                            }
                        },
                        onError: (error) => {
                            toast.error(`Error creating admin: ${error.message || 'Unknown error'}`);
                        }
                    }
                );
            }
            handleCloseModal();
        } catch (error) {
            toast.error(`Error ${isEditMode ? 'updating' : 'creating'} admin: ${error.message || 'Unknown error'}`);
        }
    };

    if (isPending || isLoadingTypes) {
        return (
            <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">
                <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                    <div className="flex flex-col pr-px pl-6 w-full max-md:pl-5 max-md:max-w-full">
                        <h1 className="self-start text-lg font-semibold">List of Admins</h1>
                        <p>Loading...</p>
                    </div>
                </section>
            </main>
        );
    }

    return (
        <div className="h-full w-full">
            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full max-md:max-w-full">
                    <div className="flex justify-between items-center pl-6 pb-4">
                        <h1 className="text-lg font-bold">List of Admins</h1>
                        {adminType === 'superadmin' && (
                            <button
                                onClick={handleOpenCreateModal}
                                className="mr-6 px-4 py-2 bg-primary text-white rounded-md hover:bg-primaryHoverDark transition-colors"
                            >
                                Add Admin
                            </button>
                        )}
                    </div>
                    <div className="pl-5">
                        <TanStackTable
                            data={data?.data || data || []}
                            columns={columns}
                            enablePagination={true}
                        />
                    </div>
                </div>
            </section>

            {/* Admin Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-semibold">
                                {isEditMode ? 'Edit Admin' : 'Add New Admin'}
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Username
                                </label>
                                <input
                                    type="text"
                                    {...register('username', { required: 'Username is required' })}
                                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-chipBlue"
                                    placeholder="Enter username"
                                />
                                {errors.username && (
                                    <p className="text-red-500 text-sm mt-1">{errors.username.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    {...register('email', {
                                        required: 'Email is required',
                                        pattern: {
                                            value: /^\S+@\S+$/i,
                                            message: 'Invalid email format'
                                        }
                                    })}
                                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-chipBlue"
                                    placeholder="Enter email"
                                />
                                {errors.email && (
                                    <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Admin Type
                                </label>
                                <select
                                    {...register('adminType', { required: 'Admin type is required' })}
                                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-chipBlue"
                                >
                                    <option value="">Select Admin Type</option>
                                    {adminTypes?.map((type) => (
                                        <option key={type._id} value={type._id}>
                                            {type.adminType}
                                        </option>
                                    ))}
                                </select>
                                {errors.adminType && (
                                    <p className="text-red-500 text-sm mt-1">{errors.adminType.message}</p>
                                )}
                            </div>

                            <div className="flex justify-end space-x-2 pt-4">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 border border-border rounded-md text-secondary hover:bg-border transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isCreating || isUpdating}
                                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primaryHoverDark transition-colors disabled:opacity-50"
                                >
                                    {(isCreating || isUpdating) ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-right"
                autoClose={3000}
            />
        </div>
    );
}

export default ListAdmins;